import {ITableColumn} from '../../types';
import {ReactNode} from 'react';

export type RatePlanDimension = { name: string, value: string }

export type ChargeTier = {
    // FNP-836: the API declares `lowerBand: Int!` / `upperBand: Int`. These were typed
    // `string` here (matching the old String! schema, which had no validation at all).
    // Bands are INCLUSIVE on both ends — q falls in a tier iff lowerBand <= q <= upperBand
    // — so the next tier's lowerBand must be the previous upperBand + 1, and the first
    // tier must start at 1.
    lowerBand: number
    // NULL/undefined means UNBOUNDED, and is legal only on the TOP tier.
    upperBand?: number | null
    // The API declares `rate: Int!`. This was typed `string`, and Mantine's number input
    // can hand back a string ("500"), so AppSync rejected the mutation with
    // "Variable 'rate' has an invalid value". Typed as number here and coerced at the
    // service boundary — see RatePlansService.toChargeTierInput.
    rate: number
}

/** FNP-834: RatePlan lifecycle. Status is AUTHORITATIVE; the effective dates SCHEDULE. */
export enum RatePlanStatus {
    DRAFT = 'DRAFT',
    ACTIVE = 'ACTIVE',
    RETIRED = 'RETIRED',
}

export enum BillablePeriod {
    MONTH = 'month',
    QUARTER = 'quarter',
    YEAR = 'year',
}

/**
 * @deprecated FNP-802 — superseded by the BillableUnit ENTITY (features/billable-units).
 * These were hardcoded labels backed by no record. Retained only so existing rate plans
 * with a legacy `billableUnit` string still render during the migration.
 */
export enum BillableUnit {
    DOMAIN_NAME = 'domain_name',
    REGISTRAR = 'registrar',
    EMAIL_ALIAS = 'email_alias',
    USER = 'user',
    TRANSFER = 'transfer',
    STORAGE = 'storage',
    SERVICE = 'service',
    CUSTOM = 'custom',
}

export type RatePlan = {
    id: string,
    name: string,
    /** @deprecated FNP-802 — legacy free-text label; use billableUnitId. */
    billableUnit?: BillableUnit,
    /** FNP-802: reference to the BillableUnit record that carries the meter + factor. */
    billableUnitId?: string,
    billablePeriod: BillablePeriod,
    chargeTiers: ChargeTier[],
    /**
     * FNP-834: lifecycle. Selectable at subscription-create iff
     *   status === ACTIVE && now >= effectiveFrom && (effectiveTo == null || now <= effectiveTo)
     * Optional because rows predating FNP-834 carry nulls until the console backfill.
     */
    status?: RatePlanStatus,
    effectiveFrom?: string,
    /** Null/absent means OPEN-ENDED — the common case. */
    effectiveTo?: string,
    createdByUsername?: string,
    createdBy?: { givenName: string, familyName: string },
    created?: string,
    updated?: string,
}


export interface IRatePlanTableColumn extends ITableColumn {
    render?: (item: RatePlan) => ReactNode;
}
